/**
 * DNW ERP — тема ConfigProvider для нового вигляду.
 *
 * Значення 1:1 з `handoff/antd-theme.ts` і мусять збігатися з токенами
 * у `src/index.css`. Нових кольорів поза цими двома файлами не вводити.
 */
import type { ThemeConfig } from 'antd';

export const dnwTheme: ThemeConfig = {
  token: {
    // Стек 1:1 з прототипу — див. коментар у index.css.
    fontFamily: "'Instrument Sans', sans-serif",
    fontSize: 13.5,
    lineHeight: 1.45,

    colorPrimary: '#8B3A0F',
    colorLink: '#8B3A0F',
    colorLinkHover: '#6B2C0A',

    colorBgLayout: '#F4F2EE',
    colorBgContainer: '#FFFFFF',
    colorBgElevated: '#FFFFFF',

    colorText: '#1C1915',
    colorTextSecondary: '#57503F',
    colorTextTertiary: '#8A8272',
    colorTextQuaternary: '#A29A88',

    colorBorder: '#DED3BD',
    colorBorderSecondary: '#E8E4DB',

    colorSuccess: '#2E6B3E',
    colorWarning: '#B45309',
    colorError: '#B42318',
    colorInfo: '#175CD3',

    borderRadius: 8,
    borderRadiusLG: 14,
    borderRadiusSM: 6,
    borderRadiusXS: 4,

    controlHeight: 36,
    controlHeightLG: 42,
    controlHeightSM: 30,

    // Тіні в прототипі майже непомітні — картки тримає межа, не тінь.
    boxShadow: '0 4px 12px rgba(28,25,21,0.08)',
    boxShadowSecondary: '0 1px 2px rgba(28,25,21,0.03)',
    boxShadowTertiary: '0 1px 2px rgba(28,25,21,0.03)',

    wireframe: false,
  },

  components: {
    Layout: {
      siderBg: '#E7E2D7',
      headerBg: 'rgba(251,250,247,0.85)',
      bodyBg: '#F4F2EE',
      headerPadding: '0 28px',
      headerHeight: 58,
    },

    Menu: {
      itemBg: 'transparent',
      subMenuItemBg: 'transparent',
      itemSelectedBg: '#F7EBDD',
      itemSelectedColor: '#6B2C0A',
      itemColor: '#57503F',
      itemHoverBg: '#DBD5C7',
      itemHoverColor: '#1C1915',
      itemBorderRadius: 8,
      itemHeight: 34,
      itemMarginInline: 0,
      groupTitleColor: '#5A5344',
      groupTitleFontSize: 11,
      iconSize: 0, // іконок у пунктах меню немає — активний стан дає лівий бар 3×16
    },

    Button: {
      fontWeight: 600,
      primaryShadow: '0 1px 2px rgba(139,58,15,0.3)',
      defaultShadow: 'none',
      dangerShadow: 'none',
      defaultBg: '#FFFFFF',
      defaultColor: '#1C1915',
      defaultBorderColor: '#DED3BD',
      defaultHoverBg: '#F1EDE3',
      defaultHoverBorderColor: '#C9BB9F',
      defaultHoverColor: '#1C1915',
      borderRadius: 9,
      paddingInline: 16,
    },

    Table: {
      headerBg: '#FBFAF7',
      headerColor: '#A29A88',
      headerSplitColor: 'transparent',
      rowHoverBg: '#FBFAF7',
      borderColor: '#F1EDE3',
      cellPaddingBlock: 12,
      cellPaddingInline: 10, // у прототипі колонки розділені зазором 12px, не широкими полями
      headerBorderRadius: 14,
      footerBg: '#FBFAF7',
    },

    Card: {
      headerBg: 'transparent',
      borderRadiusLG: 14,
      paddingLG: 20,
      colorBgContainer: '#FFFFFF',
      colorBorderSecondary: '#E8E4DB',
    },

    Input: {
      activeShadow: '0 0 0 3px rgba(139,58,15,0.12)',
      activeBorderColor: '#8B3A0F',
      hoverBorderColor: '#C9BB9F',
      paddingBlock: 7,
      colorBgContainer: '#FFFFFF',
      borderRadius: 8,
    },

    InputNumber: {
      activeShadow: '0 0 0 3px rgba(139,58,15,0.12)',
      activeBorderColor: '#8B3A0F',
      colorBgContainer: '#FFFFFF',
    },

    Select: {
      optionSelectedBg: '#F7EBDD',
      optionSelectedColor: '#6B2C0A',
      optionSelectedFontWeight: 600,
      colorBgContainer: '#FFFFFF',
      activeBorderColor: '#8B3A0F',
      hoverBorderColor: '#C9BB9F',
    },

    DatePicker: {
      activeShadow: '0 0 0 3px rgba(139,58,15,0.12)',
      activeBorderColor: '#8B3A0F',
      cellActiveWithRangeBg: '#F7EBDD',
      colorBgContainer: '#FFFFFF',
    },

    Tag: {
      defaultBg: '#F1EFE9',
      defaultColor: '#57503F',
      borderRadiusSM: 999, // статус-чипи в прототипі — пігулки, не прямокутники
    },

    Modal: {
      borderRadiusLG: 16,
      titleFontSize: 16,
      titleColor: '#1C1915',
      contentBg: '#FFFFFF',
      headerBg: '#FFFFFF',
      footerBg: '#FFFFFF',
      boxShadow: '0 24px 64px rgba(28,25,21,0.25)',
    },

    Drawer: {
      colorBgElevated: '#FFFFFF',
      paddingLG: 24,
    },

    Tabs: {
      inkBarColor: '#8B3A0F',
      itemSelectedColor: '#1C1915',
      itemHoverColor: '#1C1915',
      itemColor: '#8A8272',
      titleFontSize: 13.5,
      horizontalItemPadding: '9px 16px',
      // У прототипі відступ під вкладками дає батьківський flex (`gap:16px`),
      // тому в handoff-темі тут нуль. В antd такого батька немає — без цих
      // 16px картки прилипають до смуги вкладок.
      horizontalMargin: '0 0 16px 0',
    },

    Segmented: {
      itemSelectedBg: '#FFFFFF',
      itemSelectedColor: '#1C1915',
      itemColor: '#8A8272',
      trackBg: '#F1EDE3',
      borderRadius: 9,
    },

    Progress: {
      defaultColor: '#8B3A0F',
      remainingColor: '#EFEBE2',
    },

    Dropdown: {
      borderRadiusLG: 12,
      paddingBlock: 6,
      controlItemBgHover: '#F1EDE3',
    },

    Tooltip: {
      colorBgSpotlight: '#1C1915',
      borderRadius: 8,
    },

    Form: {
      labelColor: '#57503F',
      labelFontSize: 12,
      verticalLabelPadding: '0 0 5px',
      itemMarginBottom: 14,
    },

    Divider: {
      colorSplit: '#F1EDE3',
    },

    Alert: {
      colorInfoBg: '#EAF1FE',
      colorInfoBorder: '#C7D9F5',
      colorSuccessBg: '#EAF2EA',
      colorSuccessBorder: '#C9DCC9',
      colorWarningBg: '#FDF0D9',
      colorWarningBorder: '#F2DBAB',
      colorErrorBg: '#FEF3F2',
      colorErrorBorder: '#FECDCA',
      borderRadiusLG: 10,
    },

    Badge: {
      colorError: '#F5C518', // бейдж рекламацій — жовтий, не червоний
      colorTextLightSolid: '#1C1915',
    },

    Statistic: {
      titleFontSize: 11,
      contentFontSize: 29,
    },

    Checkbox: {
      colorPrimary: '#8B3A0F',
      borderRadiusSM: 4,
    },

    Radio: {
      colorPrimary: '#8B3A0F',
    },

    Pagination: {
      itemActiveBg: '#1C1915',
      itemBg: '#FFFFFF',
      colorPrimary: '#FBF8F2',
      borderRadius: 7,
    },

    Empty: {
      colorTextDescription: '#8A8272',
    },

    Spin: {
      colorPrimary: '#8B3A0F',
    },
  },
};
